const LANDMARK_TAGS = new Set([
	'NAV',
	'MAIN',
	'HEADER',
	'FOOTER',
	'ASIDE',
	'SECTION',
	'FORM',
])

const LANDMARK_ROLES = new Set([
	'navigation',
	'main',
	'banner',
	'contentinfo',
	'complementary',
	'region',
	'search',
	'form',
])

const ACCESSIBE_INJECTION_SELECTORS = [
	'[id^="sro-"]',
	'.acsb-sr-only',
	'[data--position]',
	'span[style*="width: 1px"]',
	'span[style*="width:1px"]',
].join(', ')

const HIDDEN_TEXT_SELECTORS = [
	ACCESSIBE_INJECTION_SELECTORS,
	'.sr-only',
	'[aria-hidden="true"]',
].join(', ')

const TEXT_LINK_ACSB_PROPS = {
	'data-acsb-navigable': 'true',
} as const

export { TEXT_LINK_ACSB_PROPS }

const isLandmarkElement = (element: Element): boolean => {
	if (LANDMARK_TAGS.has(element.tagName)) return true
	const role = element.getAttribute('role')
	return role ? LANDMARK_ROLES.has(role) : false
}

const getVisibleText = (element: HTMLElement): string => {
	const clone = element.cloneNode(true) as HTMLElement
	clone.querySelectorAll(HIDDEN_TEXT_SELECTORS).forEach((node) => node.remove())
	return clone.textContent?.replace(/\s+/g, ' ').trim() ?? ''
}

/**
 * Removes aria-label from anchor tags in CMS HTML when visible link text is present.
 * The CMS "aria_label" field stores address markup; editors may add aria-label by mistake.
 */
export const stripAriaLabelFromAnchorTags = (html: string): string => {
	return html
		.replace(/\saria-label=(["'])(?:\\.|(?!\1)[^\\])*\1/gi, '')
		.replace(/\saria-labelledby=(["'])(?:\\.|(?!\1)[^\\])*\1/gi, '')
		.replace(/\stitle=(["'])(?:\\.|(?!\1)[^\\])*\1/gi, '')
}

/**
 * Prepare CMS HTML links so accessiBe does not override visible link text.
 */
export const prepareCmsLinksForAccessibility = (html: string): string => {
	let result = stripAriaLabelFromAnchorTags(html).replace(
		/<a(?![^>]*\bdata-acsb-navigable=)([^>]*)>/gi,
		'<a data-acsb-navigable="true"$1>',
	)

	result = result.replace(
		/<a([^>]*\btarget=(["'])_blank\2[^>]*)>([\s\S]*?)<\/a>/gi,
		(match, attrs, _quote, inner) => {
			if (
				inner.includes('sr-only') ||
				inner.includes('opens in new tab') ||
				inner.includes('opens in a new tab')
			) {
				return match
			}

			return `<a${attrs}>${inner}<span class="sr-only"> (opens in new tab)</span></a>`
		},
	)

	return result
}

/**
 * Removes accessiBe label overrides from interactive elements that already expose visible text.
 */
export const fixTextLinkAriaLabels = (root: ParentNode = document.body): void => {
	root.querySelectorAll('a, button').forEach((element) => {
		const htmlElement = element as HTMLElement

		if (isLandmarkElement(htmlElement)) return

		const visibleText = getVisibleText(htmlElement)
		if (!visibleText) return

		htmlElement.removeAttribute('aria-label')
		htmlElement.removeAttribute('aria-labelledby')
		htmlElement.removeAttribute('title')

		htmlElement
			.querySelectorAll(ACCESSIBE_INJECTION_SELECTORS)
			.forEach((node) => node.remove())
	})
}

export const startAccessiBeLabelFixLoop = (): (() => void) => {
	fixTextLinkAriaLabels(document.body)

	const timeouts = [100, 500, 1500, 3000, 5000, 10000].map((delay) =>
		window.setTimeout(() => fixTextLinkAriaLabels(document.body), delay),
	)

	return () => {
		timeouts.forEach((timeoutId) => window.clearTimeout(timeoutId))
	}
}
